import React, {
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type KeyboardEvent,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import PropTypes from 'prop-types';
import styledC from 'styled-components';

import { IconButton } from '@adsk/alloy-react-button';
import CloseButton from '@adsk/alloy-react-close-button';
import {
  polymorphicForwardRef,
  useControlled,
  useInstanceId,
} from '@adsk/alloy-react-helpers';
import { useTheme } from '@adsk/alloy-react-styles';
import {
  asPropType,
  type StyleProp,
  stylePropType,
} from '@adsk/alloy-react-theme';

import { getElementSpecificProps } from './helpers';
import useOpenableTag from './hooks/useOpenableTag';

type TagOwnProps = {
  disabled?: boolean;
  defaultSelected?: boolean;
  selected?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onClose?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onIsOpenChange?: () => void;
  isOpen?: boolean;
  style?: StyleProp;
  className?: string;
  children?: React.ReactNode;
};

// Base as styled components to avoid babel-plugin-styled-components scope issue
// See: https://github.com/styled-components/babel-plugin-styled-components/issues/347
const StyledLabel = styledC.label``;

/**
 * Tags are compact elements that can be used to represent small blocks of information.
 * They are descriptive words to either filter or identify the content, and can also be a good alternative to toggle switches.
 */
const Tag = polymorphicForwardRef<'label', TagOwnProps>(
  (
    {
      disabled = false,
      selected: selectedProp,
      defaultSelected = false,
      onChange: onChangeProp,
      onClose,
      onIsOpenChange,
      isOpen,
      children,
      className,
      style,
      as,
      ...props
    },
    ref,
  ) => {
    const closeRef = useRef<HTMLButtonElement>(null);

    const theme = useTheme();
    const tagTokens = theme.tokens?.component?.tag;
    const containerTokens = tagTokens?.container;

    const id = `alloy-tag-${useInstanceId()}`;

    const [selected, setSelected] = useControlled<boolean>(
      defaultSelected ?? false,
      selectedProp,
    );

    const tagStyle = useMemo(
      () => ({
        ...containerTokens?._,
        ...(selected && containerTokens?.selected),
        ...(disabled && containerTokens?.disabled),
        ...(disabled && !selected && containerTokens?.disabledUnselected),
      }),
      [containerTokens, selected, disabled],
    );

    const onChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        if (!disabled) {
          onChangeProp && onChangeProp(event);
          setSelected(!selected);
        }
      },
      [disabled, onChangeProp, selected, setSelected],
    );

    const {
      openable,
      onKeyDown: onOpenableKeyDown,
      getOpenButtonProps,
    } = useOpenableTag({
      isOpen,
      disabled,
      onIsOpenChange,
      style: tagTokens?.closeButton,
    });

    const onKeyDown = useCallback(
      (event: KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          const input = document.getElementById(id) as HTMLInputElement;

          input.click();
        } else if (event.key === 'Backspace' || event.key === 'Delete') {
          closeRef.current?.click();
        }

        onOpenableKeyDown(event);
      },
      [id, onOpenableKeyDown],
    );

    const buttonType = (props as ButtonHTMLAttributes<HTMLButtonElement>).type;

    const elementSpecificProps = useMemo(
      () =>
        getElementSpecificProps({
          as,
          htmlFor: id,
          disabled,
          type: buttonType,
        }),
      [as, disabled, id, buttonType],
    );

    return (
      <StyledLabel
        as={as}
        ref={ref}
        className={className}
        {...elementSpecificProps}
        // eslint-disable-next-line react/forbid-component-props
        css={[
          tagStyle,
          as === 'button' && {
            verticalAlign: 'middle',
            textAlign: 'left',
          },
          style,
        ]}
        onKeyDown={onKeyDown}
        {...props}
      >
        <input
          id={id}
          value={selected.toString()}
          onChange={onChange}
          type="checkbox"
          checked={selected}
          disabled={disabled}
          css={[
            {
              display: 'none',
            },
          ]}
        />
        {children}
        {onClose && (
          <CloseButton
            ref={closeRef}
            style={tagTokens?.closeButton}
            tabIndex={-1}
            disabled={disabled}
            size={16}
            onClick={(e) => {
              e.preventDefault(); // prevent Tag selection
              onClose(e);
            }}
          />
        )}
        {openable && <IconButton {...getOpenButtonProps()} />}
      </StyledLabel>
    );
  },
);

Tag.displayName = 'Tag';

Tag.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /**  Tag text content. */
  children: PropTypes.any,
  /** Whether Tag is disabled */
  disabled: PropTypes.bool,
  /** Whether Tag is selected when first rendered */
  defaultSelected: PropTypes.bool,
  /** Whether Tag is selected */
  selected: PropTypes.bool,
  /** Function executed upon (un)selecting tag */
  onChange: PropTypes.func,
  /** Function executed upon closing tag */
  onClose: PropTypes.func,
  /** Whether the Tag's menu is open. Providing this prop renders the chevron toggle and enables ArrowUp/ArrowDown handling when the Tag is focused (requires `tabIndex={0}` or `as="button"` for the default `label` element). */
  isOpen: PropTypes.bool,
  /** Function executed when the open state should change (open ↔ closed). */
  onIsOpenChange: PropTypes.func,
  /**
   * The element type to render as.
   * @default 'label'
   */
  as: asPropType,
};

export default Tag;
